import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  LocalizationProvider,
  StaticDateTimePicker,
  StaticDatePicker
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Calendar } from 'lucide-react';
import './DateTimePicker.css';

interface DateTimePickerProps {
  value: string; // ISO string like "YYYY-MM-DDTHH:mm"
  onChange: (val: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  disablePast?: boolean;
  dateOnly?: boolean;
  onLiveChange?: (val: string) => void;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date & time',
  readOnly = false,
  disablePast = false,
  dateOnly = false,
  onLiveChange,
}) => {
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

  // Convert ISO string to dayjs
  const initialDayjs = value ? dayjs(value) : null;
  const [tempValue, setTempValue] = useState<Dayjs | null>(initialDayjs);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#059669", // App theme green
      },
      secondary: {
        main: "#e9fff6",
      },
      background: {
        paper: "transparent",
      }
    },
    typography: {
      fontFamily: 'inherit',
    }
  });

  const handleAccept = () => {
    if (tempValue) {
      if (dateOnly) {
        onChange(tempValue.format("YYYY-MM-DD"));
      } else {
        onChange(tempValue.format("YYYY-MM-DDTHH:mm"));
      }
    }
    setShowDatePicker(false);
  };

  const handleCancel = () => {
    setTempValue(value ? dayjs(value) : null);
    setShowDatePicker(false);
  };

  const inputFormat = dateOnly ? "DD/MM/YYYY" : "DD/MM/YYYY hh:mm A";

  return (
    <>
      <div
        className={`${readOnly ? "disable-" : ""}daypicker-button-dp`}
        onClick={() => {
          if (!readOnly) {
            setShowDatePicker(true);
            setTempValue(value ? dayjs(value) : dayjs());
          }
        }}
      >
        {value ? (
          dayjs(value).format(inputFormat)
        ) : (
          <div style={{ color: "#94a3b8" }}>{placeholder}</div>
        )}{" "}
        <Calendar className="dropdown-icon" size={18} color="#64748b" />
      </div>

      {showDatePicker && (
        <>
          <div
            className="faded-backdrop"
            onClick={() => setShowDatePicker(false)}
          />
          <div className="custom-popover-dp">
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {dateOnly ? (
                  <StaticDatePicker
                    defaultValue={dayjs()}
                    value={tempValue}
                    onChange={(newValue) => {
                      setTempValue(newValue);
                      if (onLiveChange && newValue) {
                        onLiveChange(newValue.format("YYYY-MM-DD"));
                      }
                    }}
                    disablePast={disablePast}
                    slotProps={{
                      actionBar: {
                        actions: [],
                      },
                    }}
                  />
                ) : (
                  <StaticDateTimePicker
                    defaultValue={dayjs()}
                    value={tempValue}
                    onChange={(newValue) => {
                      setTempValue(newValue);
                      if (onLiveChange && newValue) {
                        onLiveChange(newValue.format("YYYY-MM-DDTHH:mm"));
                      }
                    }}
                    disablePast={disablePast}
                    slotProps={{
                      actionBar: {
                        actions: [],
                      },
                    }}
                  />
                )}
              </LocalizationProvider>
            </ThemeProvider>

            <div className="custom-button-dp">
              <div onClick={handleCancel}>CANCEL</div>
              <div onClick={handleAccept}>OK</div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DateTimePicker;
